/* globals data, serverParams */
import 'bam-node-baseball-savant/swing-timing/swing-timing-app.tsx'
// import 'bam-node-baseball-savant/swing-timing/scatter-simple'

import Table, {
    Header,
    RowValue,
    SortDirection,
} from 'bam-node-savant-table/table-ts.ts'
import { guff, displayFn } from 'bam-node-baseball-savant/utils/guff'
import type { Dimension } from 'bam-node-baseball-savant/swing-timing/swing-timing-chart'

import { MIN_SWINGS } from 'bam-node-baseball-savant/swing-timing/utils/constants.ts'
import initStaticHeaders, {
    conditionalAddStaticHeader,
} from '../utils/static-headers'
import { slideUp, slideDown } from '../utils/leaderboard-drawer-helpers'
import { playerDisplayFnById } from '../utils/bat-tracking-table-helpers'
import concatGroupedHeaders from '../utils/concat-grouped-headers'
import getBatTrackingFirstHeaders from '../utils/bat-tracking-first-headers.ts'
import getGroupedHeaders from '../utils/get-grouped-headers'
import pitchTypeFn from '../utils/pitch-type-fn.ts'
import { TRIGGER_SWING_TIMING_VISUAL_ADD } from '../swing-path/constants.ts'
import {
    SwingTimingData,
    SwingTimingDrawer,
    Pinnable,
} from '../swing-path/types/globals.ts'

// Declare page-specific global variable with type
declare const SWING_TIMING_leagueAverageDataFilteredGrouped: SwingTimingData[]

type PinnableData = Pinnable<SwingTimingData>

let showLeagueAverage = false
const tableDivId = 'bat_tracking_table'
const $table = document.getElementById(tableDivId)

const domColumns: {
    x: NodeListOf<Element> | null
    y: NodeListOf<Element> | null
    z: NodeListOf<Element> | null
} = { x: null, y: null, z: null }
const isNotPlayer = new Set(['batting-team', 'pitching-team', 'league']).has(
    serverParams.type
)
const pitchHandRedundancy =
    serverParams.type === 'pitcher' &&
    serverParams.secondarySplit.includes('pitch_hand')
const batSideRedundancy =
    serverParams.type === 'batter' &&
    serverParams.secondarySplit.includes('bat_side')

const isLeague = Boolean(serverParams.type === 'league')

const batterTypes = new Set(['batter', 'batting-team'])
const isBatter = batterTypes.has(serverParams.type)

const hideSide = isNotPlayer || batSideRedundancy || pitchHandRedundancy

const firstHeaders = getBatTrackingFirstHeaders(
    serverParams.type,
    serverParams.secondarySplit,
    'statcast_bat_tracking_swing_timing'
)

const threshold = 0.045

const displayFnFactory =
    (prop: keyof PinnableData) => (val: RowValue, row: PinnableData) => {
        const formatted = guff(val, 'swing_timing_dist')

        if (Number(row[prop]) < threshold) {
            return `<span class="de-emphasize-cell">${formatted}</span>`
        }

        return formatted
    }

const headers: Header<PinnableData>[] = [
    ...firstHeaders,

    {
        name: 'Avg. Miss<br/>Dist. (inches)',
        field: 'miss_distance',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn,
        toolTip: 'Swing and Miss Only',
        // Art not science
        // highlight: [0.5, 2, 4.5],
        highlightDynamic: true,
        highlightDynamicFlip: isBatter,
    },
    // {
    //     name: 'Avg. Miss<br/>Dist. (Smoltz)',
    //     field: 'miss_distance_smoltz',
    //     headerClass: `th-component-header align-center th-vertical-bottom`,
    //     fieldClass: `tr-data align-center`,
    //     defaultSortDir: 'desc',
    //     type: 'number',
    //     displayFn: (val) => guff(val, 'miss_distance'),
    //     toolTip: 'Zeroed on BBE',
    //     // Art not science
    //     // highlight: [0.5, 2, 4.5],
    //     highlightDynamic: true,
    // },
    {
        name: 'Flawed<br/> Swing %',
        field: 'flawed_percent',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'swing_timing_percent'),
        toolTip:
            'The percentage of all swings in which the batter whiffs and the swing is not any of "Lined up," "Centered" or "On Time"',
    },
    {
        name: 'Perfect<br/>Contact %',
        field: 'perfect_percent',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'swing_timing_percent'),
        toolTip:
            'Percentage of all swings in which the batter makes contact and the swing is "Lined up," "Centered" and "On Time"',
    },
    {
        name: 'Over',
        field: 'over_percent',
        headerClass: `th-component-header align-center th-vertical-bottom border-left swing-timing-col-z`,
        fieldClass: `tr-data align-center border-left swing-timing-col-z`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'swing_timing_percent'),
    },
    {
        name: 'Avg. Over</br>Dist. (inches)',
        field: 'avg_z_over',
        headerClass: `th-component-header align-center th-vertical-bottom swing-timing-col-z`,
        fieldClass: `tr-data align-center swing-timing-col-z`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: displayFnFactory('over_percent'),
    },
    {
        name: 'Lined Up',
        field: 'lined_up_percent',
        headerClass: `th-component-header align-center th-vertical-bottom dashed-left swing-timing-col-z`,
        fieldClass: `tr-data align-center dashed-left swing-timing-col-z`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'swing_timing_percent'),
    },
    // {
    //     name: 'Avg. Lined Up</br>Dist. (inches)',
    //     field: 'avg_z_lined_up',
    //     headerClass: `th-component-header align-center th-vertical-bottom swing-timing-col-z`,
    //     fieldClass: `tr-data align-center swing-timing-col-z`,
    //     defaultSortDir: 'desc',
    //     type: 'number',
    //     displayFn: (val) => guff(val, 'swing_timing_dist'),
    // },
    {
        name: 'Under',
        field: 'under_percent',
        headerClass: `th-component-header align-center th-vertical-bottom dashed-left swing-timing-col-z`,
        fieldClass: `tr-data align-center dashed-left swing-timing-col-z`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'swing_timing_percent'),
    },
    {
        name: 'Avg. Under <br/>Dist. (inches)',
        field: 'avg_z_under',
        headerClass: `th-component-header align-center th-vertical-bottom swing-timing-col-z`,
        fieldClass: `tr-data align-center swing-timing-col-z`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: displayFnFactory('under_percent'),
    },
    {
        name: 'Tied Up',
        field: 'tied_up_percent',
        headerClass: `th-component-header align-center th-vertical-bottom border-left swing-timing-col-x`,
        fieldClass: `tr-data align-center border-left swing-timing-col-x`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'swing_timing_percent'),
    },
    {
        name: 'Avg. Tied Up <br/>Dist. (inches)',
        field: 'avg_x_tied_up',
        headerClass: `th-component-header align-center th-vertical-bottom swing-timing-col-x`,
        fieldClass: `tr-data align-center swing-timing-col-x`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: displayFnFactory('tied_up_percent'),
    },
    {
        name: 'Centered',
        field: 'centered_percent',
        headerClass: `th-component-header align-center th-vertical-bottom dashed-left swing-timing-col-x`,
        fieldClass: `tr-data align-center dashed-left swing-timing-col-x`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'swing_timing_percent'),
    },
    // {
    //     name: 'Avg. Centered</br>Dist. (inches)',
    //     field: 'avg_x_centered',
    //     headerClass: `th-component-header align-center th-vertical-bottom swing-timing-col-x`,
    //     fieldClass: `tr-data align-center swing-timing-col-x`,
    //     defaultSortDir: 'desc',
    //     type: 'number',
    //     displayFn: (val) => guff(val, 'swing_timing_dist'),
    // },
    {
        name: 'Flailed',
        field: 'flailed_percent',
        headerClass: `th-component-header align-center th-vertical-bottom dashed-left swing-timing-col-x`,
        fieldClass: `tr-data align-center dashed-left swing-timing-col-x`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'swing_timing_percent'),
    },
    {
        name: 'Avg. Flailed</br>Dist. (inches)',
        field: 'avg_x_flail',
        headerClass: `th-component-header align-center th-vertical-bottom swing-timing-col-x`,
        fieldClass: `tr-data align-center swing-timing-col-x`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: displayFnFactory('flailed_percent'),
    },
    {
        name: 'Early',
        field: 'early_percent',
        headerClass: `th-component-header align-center th-vertical-bottom border-left swing-timing-col-y`,
        fieldClass: `tr-data align-center border-left swing-timing-col-y`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'swing_timing_percent'),
    },
    {
        name: 'Avg. Early</br>Time (ms)',
        field: 'avg_y_early',
        headerClass: `th-component-header align-center th-vertical-bottom swing-timing-col-y`,
        fieldClass: `tr-data align-center swing-timing-col-y`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: displayFnFactory('early_percent'),
    },
    {
        name: 'On Time',
        field: 'on_time_percent',
        headerClass: `th-component-header align-center th-vertical-bottom dashed-left swing-timing-col-y`,
        fieldClass: `tr-data align-center dashed-left swing-timing-col-y`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'swing_timing_percent'),
    },
    // {
    //     name: 'Avg. On Time<br/>(Time ms.)',
    //     field: 'avg_y_on_time',
    //     headerClass: `th-component-header align-center th-vertical-bottom swing-timing-col-y`,
    //     fieldClass: `tr-data align-center swing-timing-col-y`,
    //     defaultSortDir: 'desc',
    //     type: 'number',
    //     displayFn: (val) => guff(val, 'swing_timing_dist'),
    // },
    {
        name: 'Late',
        field: 'late_percent',
        headerClass: `th-component-header align-center th-vertical-bottom border-left dashed-left swing-timing-col-y`,
        fieldClass: `tr-data align-center border-left dashed-left swing-timing-col-y`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'swing_timing_percent'),
    },
    {
        name: 'Avg. Late<br/>(Time ms.)',
        field: 'avg_y_late',
        headerClass: `th-component-header align-center th-vertical-bottom swing-timing-col-y`,
        fieldClass: `tr-data align-center swing-timing-col-y`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: displayFnFactory('late_percent'),
    },
    {
        name: 'Swings',
        field: 'n_swings',
        headerClass: `th-component-header align-center th-vertical-bottom border-left`,
        fieldClass: `tr-data align-center border-left`,
        defaultSortDir: 'desc',
        type: 'number',
        toolTip: 'Number of tracked swings. All values starts at 2023 ASG',
        displayFn: (val) => guff(val, 'swings'),
    },
    {
        name: 'Whiff<br/>Rate',
        field: 'whiff_rate',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        toolTip:
            'Percentage of all tracked swings that are whiffs. All values starts at 2023 ASG',
        displayFn: (val) => guff(val, 'whiff_rate_percent'),
    },
    {
        name: 'Competitive<br />Rate',
        field: 'competitive_percent',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        toolTip: `The fastest 90% of a player's swings, plus any 60+ MPH swings resulting in an exit velocity of 90+ MPH, are deemed to be his 'competitive' swings. All values start 2023 ASG.`,
        displayFn: (val) => guff(val, 'swings_qualified_percent'),
    },
    {
        name: '',
        field: 'uniqueId',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        sortable: false,
        displayFn: (val, row) =>
            row.n_swings >= MIN_SWINGS && !row.pinned
                ? `
                <button class="btn btn-default test-viz" data-unique-id="${row.uniqueId}">
                    Visual
                </button>`
                : '',
    },
    {
        name: '',
        field: 'uniqueId',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        sortable: false,
        displayFn: (val, row) =>
            row.n_swings >= MIN_SWINGS && !row.pinned
                ? `
            <span data-video-icon="" style="inline-block; padding: 2px; border-radius: 50%; border: 2px solid #c2c2c2;">
                <img src="/site-core/images/video.png" style="width: 18px; cursor: pointer;">
            </span>`
                : '',
    },
]

const drawerHeaders: Header<SwingTimingDrawer>[] = [
    {
        name: 'Pitcher',
        field: 'p_name_display_first_last',
        headerClass: `th-component-header align-left th-vertical-bottom`,
        fieldClass: `tr-data align-left`,
        displayFn: (val, { pitcher }) =>
            playerDisplayFnById(String(val), Number(pitcher), {
                firstInit: true,
            }),
        sortable: false,
    },
    {
        name: 'Batter',
        field: 'b_name_display_first_last',
        headerClass: `th-component-header align-left th-vertical-bottom`,
        fieldClass: `tr-data align-left`,
        displayFn: (val, { savant_batter_id }) =>
            playerDisplayFnById(String(val), Number(savant_batter_id), {
                firstInit: true,
            }),
        sortable: false,
    },
    {
        name: '',
        field: 'pitch_type',
        headerClass: `th-component-header align-left th-vertical-bottom`,
        fieldClass: `tr-data align-left`,
        sortable: false,
        displayFn: pitchTypeFn,
    },
    {
        name: 'Date',
        field: 'game_date',
        headerClass: `th-component-header align-left th-vertical-bottom`,
        fieldClass: `tr-data align-left`,
        displayFn: (val) => String(val).slice(0, 10),
        sortable: false,
    },

    {
        name: 'Miss Distance',
        field: 'miss_distance_inches',
        headerClass: `th-component-header align-right th-vertical-bottom`,
        fieldClass: `tr-data align-right border-right`,
        type: 'number',
        displayFn: (val) =>
            `${guff(
                val,
                'miss_distance'
            )}<span class="arm-glv-label">IN.</span>`,
        sortable: false,
    },
    {
        name: 'Tied Up / Flail',
        field: 'delta_batball_tiedup_neg_x2',
        headerClass: `th-component-header align-right th-vertical-bottom`,
        fieldClass: `tr-data align-right border-right`,
        type: 'number',
        customSortField: 'abs_delta_batball_tiedup_neg_x2',
        sortable: false,
        displayFn: (val, row) => {
            if (val === undefined || val === null || typeof val !== 'number') {
                // Should never get here
                return '--'
            }

            if (row.timing_x_tiedupflail === 'Centered') {
                return `<span class="arm-glv-label" style="font-weight: bold;">CENTERED&nbsp;&nbsp;&nbsp;</span>`
            }
            const formatted = guff(Math.abs(val), 'miss_distance')

            const direction = val < 0 ? 'TIED UP' : 'FLAIL&nbsp;&nbsp;'
            return `${formatted} <span class="arm-glv-label">IN. ${direction}</span>`
        },
    },
    {
        name: 'Early / Late',
        field: 'delta_batball_late_neg_y2_msec',
        headerClass: `th-component-header align-right th-vertical-bottom`,
        fieldClass: `tr-data align-right border-right`,
        type: 'number',
        customSortField: 'abs_delta_batball_late_neg_y2_msec',
        sortable: false,
        displayFn: (val, row) => {
            if (val === undefined || val === null || typeof val !== 'number') {
                // Should never get here
                return '--'
            }

            if (row.timing_y_earlylate === 'OnTime') {
                return `<span class="arm-glv-label" style="font-weight: bold;">ON TIME&nbsp;</span>`
            }

            const formatted = guff(Math.abs(val), 'miss_distance')

            const direction = val < 0 ? 'LATE&nbsp;' : 'EARLY'
            return `${formatted} <span class="arm-glv-label">MS. ${direction}</span>`
        },
    },
    {
        name: 'Over / Under',
        field: 'ball_pos_above_plane',
        headerClass: `th-component-header align-right th-vertical-bottom`,
        fieldClass: `tr-data align-right border-right`,
        type: 'number',
        customSortField: 'abs_ball_pos_above_plane',
        sortable: false,
        displayFn: (val, row) => {
            if (val === undefined || val === null || typeof val !== 'number') {
                // Should never get here
                return '--'
            }

            if (row.timing_z_overunder === 'Linedup') {
                return `<span class="arm-glv-label" style="font-weight: bold;">LINED UP&nbsp;</span>`
            }

            const formatted = guff(Math.abs(val), 'miss_distance')

            const direction = val < 0 ? 'OVER&nbsp;' : 'UNDER'
            return `${formatted} <span class="arm-glv-label">IN. ${direction}</span>`
        },
    },
    // {
    //     name: 'Tied Up / Flail',
    //     field: 'timing_x_tiedupflail',
    //     headerClass: `th-component-header align-right th-vertical-bottom`,
    //     fieldClass: `tr-data align-right`,
    // },
    // {
    //     name: 'Early / Late',
    //     field: 'timing_y_earlylate',
    //     headerClass: `th-component-header align-right th-vertical-bottom`,
    //     fieldClass: `tr-data align-right`,
    // },
    // {
    //     name: 'Over / Under',
    //     field: 'timing_z_overunder',
    //     headerClass: `th-component-header align-right th-vertical-bottom`,
    //     fieldClass: `tr-data align-right`,
    // },
    {
        name: '',
        field: 'play_id',
        sortable: false,
        headerClass: `th-component-header align-right th-vertical-bottom`,
        fieldClass: `tr-data align-right`,
        displayFn: (val) =>
            `<a href="/sporty-videos?playId=${val}" target="_blank">
                            <img src="/site-core/images/video.png" style="margin-right: 4px; width: 25px; height: 13px">
                        </a>`,
    },
    {
        name: '',
        field: 'is_swing_sword',
        headerClass: `th-component-header align-right th-vertical-bottom`,
        fieldClass: `tr-data align-right`,
        sortable: false,
        displayFn: (val, row) =>
            Number(val) === 1
                ? `<a href="/sporty-videos?playId=${row.play_id}" target="_blank">⚔️</a>`
                : '',
    },

    // 'timing_x_tiedupflail',
    // 'timing_y_earlylate',
    // 'timing_z_overunder'
]
const groupHeaders = getGroupedHeaders(serverParams.secondarySplit)

function getFirstColspan() {
    // the default
    let colspan = 4 - Number(hideSide) - Number(isLeague)

    // Add the extra group by columns
    colspan += groupHeaders.length

    return colspan
}

let currentSelected: Dimension | undefined = serverParams.showColumn

const STAT_SELECTOR_ID = 'stat-selector'

function getSecondaryHeaders() {
    const selectHTML = `
    <div class="form-group">
        <div class="savant-select" style="width: 100%">
            <select id="${STAT_SELECTOR_ID}">
                <option value="x" ${currentSelected === 'x' ? 'selected' : ''}>
                    Tied Up / Flail
                </option>
                <option value="y" ${currentSelected === 'y' ? 'selected' : ''}>
                    Early / Late
                </option>
                <option value="z" ${currentSelected === 'z' ? 'selected' : ''}>
                    Over / Under (Bat POV)
                </option>
            </select>
        </div>
    </div>
    `

    const secondaryHeaders = [
        {
            name: '',
            colspan: getFirstColspan(),
            headerClass: 'th-name-header',
        },
        {
            name: '',
            colspan: 3,
            headerClass: 'th-secondary-em',
        },
        {
            name: selectHTML,
            colspan: 5,
            headerClass: 'th-title-header',
        },
        {
            name: '',
            colspan: 3,
            headerClass: 'th-secondary-em',
        },
    ]

    return secondaryHeaders
}

function loadTable(filteredData: SwingTimingData[]) {
    const withPinnedData: PinnableData[] = [
        ...filteredData,
        ...SWING_TIMING_leagueAverageDataFilteredGrouped.map((d) => ({
            ...d,
            pinned: true,
        })),
    ]

    const width = $table?.offsetWidth || 0
    let repeatHeader: number | undefined
    if (width <= 768) {
        repeatHeader = 15
    }

    const URLParams = new URLSearchParams(window.location.search)
    const defaultSort: keyof SwingTimingData =
        (URLParams.get('sortColumn') as keyof SwingTimingData) ||
        'miss_distance'
    const defaultSortDirection: SortDirection = isBatter ? 'asc' : 'desc'
    const initialSortDirection: SortDirection =
        (URLParams.get('sortDirection') as SortDirection) ||
        defaultSortDirection

    // eslint-disable-next-line no-new
    const table = new Table({
        id: tableDivId,
        repeatHeader,
        headers: concatGroupedHeaders(headers, groupHeaders),
        secondaryHeaders: getSecondaryHeaders(),
        hasPinned: true,
        highlightFalsy: true,
        data: withPinnedData,
        uniqueId: 'uniqueId', // this can't have dashes in it
        defaultSort,
        initialSortDirection,
        sortClass: 'column-sort',
        showRank: true,
        showPinned: showLeagueAverage,
        sortCallback(column, _, { state }) {
            const url = new URL(window.location.href)
            url.searchParams.set('sortColumn', String(column))
            url.searchParams.set('sortDirection', state || 'desc')
            window.history.replaceState('', '', url.toString())
        },
        subDataId: 'uniqueId',
    })

    // Ugliest hack I've done for a while.
    // eslint-disable-next-line no-global-assign
    data = withPinnedData.filter((d) => !d.pinned)

    initStaticHeaders(tableDivId, withPinnedData)

    cacheDOMColumns()

    return table
}

function cacheDOMColumns() {
    domColumns.x = document.querySelectorAll('.swing-timing-col-x')
    domColumns.y = document.querySelectorAll('.swing-timing-col-y')
    domColumns.z = document.querySelectorAll('.swing-timing-col-z')
}

function addDrawerTabs($wrapperDiv: HTMLElement) {
    const drawerTabs = `
    <div class="drawer-tabs">
        <button data-tab="miss_distance" class="tab-all drawer-nav active" >
            Biggest Misses
        </button>
        <button data-tab="x" class="tab-fielder drawer-nav" >
            Tied Up / Flail
        </button>
        <button data-tab="y" class="tab-runner drawer-nav" >
            Early / Late
        </button>
        <button data-tab="z" class="tab-pitcher drawer-nav" >
            Over / Under
        </button>
    </div>
    `

    // eslint-disable-next-line no-param-reassign
    $wrapperDiv.innerHTML = drawerTabs
}

function handleTabSwitch($button: HTMLElement) {
    const { tab } = $button.dataset

    // all buttons
    $button
        .closest('.drawer-tabs')
        ?.querySelectorAll('.drawer-nav')
        .forEach(($el: Element) => {
            $el.classList.remove('active')
        })

    // add active button
    $button.classList.add('active')

    const $drawer = $button.closest('.table-drawer')

    $drawer?.querySelectorAll('.tab-pane').forEach(($el: Element) => {
        $el.classList.remove('active')
    })

    $drawer?.querySelector(`.${tab}-tab-pane`)?.classList.add('active')
}

type TableType = 'x' | 'y' | 'z' | 'miss_distance'

const columns: Record<TableType, keyof SwingTimingDrawer> = {
    x: 'delta_batball_tiedup_neg_x2',
    y: 'delta_batball_late_neg_y2_msec',
    z: 'ball_pos_above_plane',
    miss_distance: 'miss_distance_inches',
}

const titles: Record<TableType, string> = {
    x: 'Most Extreme Tied Up / Flail',
    y: 'Most Extreme Early / Late',
    z: 'Most Extreme Over / Under',
    miss_distance: 'Biggest Misses',
}

function createExtremeTable(
    $wrapperDiv: HTMLElement,
    prop: TableType,
    id: string,
    data: SwingTimingDrawer[]
) {
    const $tabWrapper = document.createElement('div')
    $tabWrapper.classList.add(`${prop}-tab-pane`)
    $tabWrapper.classList.add('tab-pane')

    if (prop === 'miss_distance') {
        $tabWrapper.classList.add('active')
    }

    const $h3Grouped = document.createElement('h3')
    // capitalize first letter
    $h3Grouped.innerText = titles[prop]
    $h3Grouped.style.marginBottom = '0'

    const $groupedTableDiv = document.createElement('div')
    $groupedTableDiv.id = `${prop}_${id}`

    $tabWrapper.appendChild($h3Grouped)
    $tabWrapper.appendChild($groupedTableDiv)

    $wrapperDiv.appendChild($tabWrapper)

    // eslint-disable-next-line no-new
    new Table({
        id: $groupedTableDiv.id,
        rowClass: 'inner-table',
        defaultSortDirection: 'desc',
        defaultSort: columns[prop],
        sortClass: 'column-sort',
        pagination: {
            enabled: false,
        },
        search: {
            enabled: false,
        },
        secondaryHeaders: [
            {
                name: '',
                colspan: 5,
                headerClass: 'th-name-header',
            },
            {
                name: 'inches',
                colspan: 1,
                headerClass: 'th-title-header th-secondary-em align-right',
            },
            {
                name: 'inches',
                colspan: 1,
                headerClass: 'th-title-header th-secondary-em align-right',
            },
            {
                name: 'milliseconds',
                colspan: 1,
                headerClass: 'th-title-header th-secondary-em align-right',
            },
            {
                name: 'inches',
                colspan: 1,
                headerClass: 'th-title-header th-secondary-em align-right',
            },
            {
                name: '',
                colspan: 2,
                headerClass: 'th-name-header',
            },
        ],
        headers: drawerHeaders,
        data,
    })
}

function isSwingTimingData(arr: unknown[]): arr is SwingTimingData[] {
    if (!arr.length) return true
    const first = arr[0]
    return (
        typeof first === 'object' &&
        first !== null &&
        'uniqueId' in first &&
        'n_swings' in first &&
        'pitch_hand_type' in first &&
        'bat_side_type' in first
    )
}

if (data && isSwingTimingData(data)) {
    const table = loadTable(data)

    const $leagueAverageToggle = document.getElementById(
        'btn-toggle-league-average'
    )

    $leagueAverageToggle?.addEventListener('click', () => {
        showLeagueAverage = !showLeagueAverage
        table.setShowPinned(showLeagueAverage)
    })

    $table?.addEventListener('click', async (e) => {
        const target = e.target as HTMLElement

        // Handle Visual Click First
        if (target?.matches('button.test-viz')) {
            const { uniqueId } = target.dataset
            const customEvent = new CustomEvent(
                TRIGGER_SWING_TIMING_VISUAL_ADD,
                {
                    detail: {
                        uniqueId,
                    },
                }
            )
            window.dispatchEvent(customEvent)
            return
        }

        const $el = target?.closest(
            'tr.default-table-row'
        ) as HTMLElement | null
        const $button = target?.closest('.drawer-nav') as HTMLElement | null
        // const $a = e.target.closest('a')

        if ($button) {
            handleTabSwitch($button)
        }

        if (!$el || $el.classList.contains('inner-table')) {
            return
        }

        const $sub = $el.nextSibling as HTMLElement | null
        const $td = $sub?.querySelector('td')
        const $wrapperDiv = document.createElement('div')

        const id = `data_${$el.dataset.id}`
        const $innerDiv = document.getElementById(id)

        // CHeck if already exists
        if ($innerDiv && $sub) {
            if ($sub.dataset.open === 'false') {
                slideDown($innerDiv, 400)
                $sub.dataset.open = 'true'
                return
            }
            slideUp($innerDiv, 400)
            $sub.dataset.open = 'false'
            return
        }

        $wrapperDiv.classList.add('table-drawer')
        $wrapperDiv.id = id
        $wrapperDiv.textContent = `Loading...${
            serverParams.type === 'league' ? ' (this may take a while)' : ''
        }`

        if ($sub) {
            $sub.style.display = 'table-row'
        }

        $td?.appendChild($wrapperDiv)

        const unique = parseUniqueId($el.dataset.id!)

        const urlParams = new URLSearchParams(window.location.search)

        Object.keys(unique).forEach((key) => {
            urlParams.set(key, String(unique[key]))
        })

        const { miss_distance: missDistance, ...dims } = (await fetch(
            `/leaderboard/services/swing-timing?${urlParams}`
        ).then((res) => res.json())) as Record<TableType, SwingTimingDrawer[]>

        const isEmpty =
            !missDistance.length &&
            Object.values(dims).every((arr) => !arr?.length)
        if (isEmpty) {
            // Ugly but functional
            $wrapperDiv.textContent = 'No Swing and Misses Found'
            return
        }

        $wrapperDiv.textContent = ''

        addDrawerTabs($wrapperDiv)

        if ($el.dataset.id) {
            createExtremeTable(
                $wrapperDiv,
                'miss_distance',
                $el.dataset.id,
                missDistance
            )
        }

        Object.keys(dims).forEach((prop) => {
            if (prop !== 'x' && prop !== 'y' && prop !== 'z') {
                // just being careful
                return
            }
            createExtremeTable($wrapperDiv, prop, id, dims[prop])
        })
    })
}

function parseUniqueId(idStr: string) {
    const groupByProperties = serverParams.secondarySplit
        // This must match the server side
        .filter((d) => d !== 'api_game_date_month_text')
        .sort((a, b) => (a > b ? 1 : -1))

    const [id, ...groupBys] = idStr.split('_')

    if (groupByProperties.length !== groupBys.length) {
        throw new Error('mismatch in groups')
    }

    const uniqueData: Record<string, string> = { id }
    groupByProperties.forEach((prop, i) => {
        uniqueData[prop] = groupBys[i]
    })

    return uniqueData
}

$table?.addEventListener('change', (e) => {
    // Just in case.
    const target = e.target as HTMLSelectElement
    if (target?.id !== STAT_SELECTOR_ID) {
        return
    }
    if ($table) {
        $table.dataset.showColumn = target.value
    }
    const url = new URL(window.location.href)
    currentSelected = target.value as Dimension
    url.searchParams.set('showColumn', target.value)
    window.history.replaceState('', '', url.toString())

    const selectedColumns = domColumns[currentSelected]
    selectedColumns?.forEach((el: Element) => {
        el.classList.add('highlight-flash')
    })

    conditionalAddStaticHeader(tableDivId)
})

// Handle exclusive checking of one pitch type group at a time
// Don't allow empty, toggle to other selection instead
const pitchSplitCheckboxes = document.querySelectorAll<HTMLInputElement>(
    '[data-exclusive-group="pitch-split"]'
)

if (pitchSplitCheckboxes.length === 2) {
    const [first, second] = pitchSplitCheckboxes

    first.addEventListener('change', () => {
        second.checked = false
    })

    second.addEventListener('change', () => {
        first.checked = false
    })
}
